import {
  makeObservable,
  action,
  observable,
  flow,
  flowResult,
  when,
  reaction,
  computed
} from 'mobx'
import algoliasearch, {SearchClient} from 'algoliasearch'
import type {CommerceAPI} from 'commerce-api'
import type {RootStore} from './RootStore'
import type {StoreModel} from './utils'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {Nullable} from '../types/utils'
import type {AvailablePaymentMethod, CustomSitePreferences} from '../types/store/common'
import { StoreDetailResponseItem } from '../types/store/storeDetails'
import { NavigationMenu, NavItem } from '../types/cms/navigation'
import { Promotion } from './ProductStore'
import {
  PAYMENT_METHOD_IDS,
  CARD_TYPES,
  OS_VENDOR,
  SupportedApplePayVersion,
  applePayScript,
  klarnaScript,
  klarnaOSMScript,
  LOCALSTORAGE_KEYS,
  clearpayScript,
  clearpaySandboxScript
} from '../utils/constants'
import { testUserAgent, asyncScriptLoader } from '../utils/utils'
import { RegionalIndexConfig } from '../types/algolia'
import { appHeaders } from 'commerce-api/dist/utils'
import BonusCardApp from '../utils/app'
import { LocalStorage } from '../utils/storages'

export class GlobalStore implements StoreModel {
  api: CommerceAPI
  rootStore: RootStore
  locale: string
  currency: string
  appHostname: string
  appOrigin: string
  deployTarget: string
  customerCacheClearInterval: number
  organizationId: string
  clientId: string
  siteId: string
  algoliaIndexBase: string
  algoliaIndexBaseDefault: string
  algoliaAppId: string
  algoliaApiKey: string
  algoliaAnalyticsAPIKey: string
  retailMediaCatalogID: string
  retailMediaContentStandardID: string
  retailMediaUrl: string
  einsteinConfiguration: {
    siteId: string
    einsteinId: string
  }
  selectedRegion: number
  selectedStoreId?: Nullable<string>
  isPromoModalOpen: {isOpen?: boolean, promotion?: Promotion}
  selectedPriceDayOffset?: number
  selectedAvailabilityDayOffset?: Nullable<number>
  loqateAPIKey: string
  recaptchaSiteKey: string
  googleMapsAPIkey: string
  customSitePreferences: CustomSitePreferences
  customSitePreferencesLoading: Set<string>
  customObjectDateOffsetAvailability: number
  customObjectDateOffsetPricing: number
  affiliateTrackingCampaign: string
  affiliateTrackingSource: string
  affiliateTrackingMedium: string
  dataLoadedForAccuratePricingAndAvailabilty: boolean
  showCheckoutSummary: boolean
  gtmId: string
  gtmServer: string
  googlePayMerchantId: string
  googlePayMerchantName: string
  klarnaClientId: string
  klarnaCollectShipping: boolean
  sfccEnv: string
  sentryInitialized: boolean
  sentryEnabled: boolean
  sentryEnv: string
  sentryDSN: string
  algoliaRefinements: Array<string>
  paypalClient: string
  searchClient: SearchClient
  appVersion: string
  alertsUrl: string
  availablePaymentMethods: Array<AvailablePaymentMethod>
  hasApplePayButtonRendered: boolean
  isRegionalIndexABTestEnabled: boolean
  regionalIndexesConfig: RegionalIndexConfig
  isShopTab: Nullable<boolean>
  hasKlarnaButtonRendered: boolean

  constructor(
    rootStore: RootStore,
    initialState: {
      locale: string
      currency: string
      appHostname: string
      appOrigin: string
      deployTarget: string
      customerCacheClearInterval: number
      organizationId: string
      clientId: string
      siteId: string
      algoliaIndexBase: string
      algoliaIndexBaseDefault: string
      algoliaAppId: string
      algoliaApiKey: string
      algoliaAnalyticsAPIKey: string
      retailMediaCatalogID: string
      retailMediaContentStandardID: string
      retailMediaUrl: string
      loqateAPIKey: string
      recaptchaSiteKey: string
      googleMapsAPIkey: string
      einsteinConfiguration: {
        siteId: string
        einsteinId: string
      }
      showCheckoutSummary: boolean
      customObjectDateOffsetAvailability: number
      customObjectDateOffsetPricing: number
      dataLoadedForAccuratePricingAndAvailabilty: boolean
      gtmId: string,
      gtmServer: string,
      googlePayMerchantId: string
      googlePayMerchantName: string
      klarnaClientId: string
      klarnaCollectShipping: string
      sfccEnv: string
      sentryInitialized: boolean
      sentryEnabled: boolean
      sentryEnv: string
      sentryDSN: string
      algoliaRefinements: Array<string>
      paypalClient: string
      customSitePreferences: CustomSitePreferences
      appVersion: string
      alertsUrl: string
      affiliateTrackingCampaign: string
      affiliateTrackingSource: string
      affiliateTrackingMedium: string
      availablePaymentMethods: Array<AvailablePaymentMethod>
      hasApplePayButtonRendered: boolean
      hasKlarnaButtonRendered: boolean
      isRegionalIndexABTestEnabled: boolean
      regionalIndexesConfig: RegionalIndexConfig
      isShopTab: Nullable<boolean>
    },
  ) {
    this.locale = initialState.locale
    this.currency = initialState.currency
    this.appHostname = initialState.appHostname
    this.appOrigin = initialState.appOrigin
    this.deployTarget = initialState.deployTarget
    this.customerCacheClearInterval = initialState.customerCacheClearInterval
    this.organizationId = initialState.organizationId
    this.clientId = initialState.clientId
    this.siteId = initialState.siteId
    this.algoliaIndexBase = initialState.algoliaIndexBase
    this.algoliaIndexBaseDefault = initialState.algoliaIndexBase
    this.einsteinConfiguration = initialState.einsteinConfiguration
    this.loqateAPIKey = initialState.loqateAPIKey
    this.recaptchaSiteKey = initialState.recaptchaSiteKey
    this.googleMapsAPIkey = initialState.googleMapsAPIkey
    this.algoliaAnalyticsAPIKey = initialState.algoliaAnalyticsAPIKey
    this.algoliaAppId = initialState.algoliaAppId
    this.algoliaApiKey = initialState.algoliaApiKey
    this.selectedStoreId = '0'
    this.retailMediaCatalogID = initialState.retailMediaCatalogID
    this.retailMediaContentStandardID = initialState.retailMediaContentStandardID
    this.retailMediaUrl = initialState.retailMediaUrl
    this.isPromoModalOpen = {isOpen: false}
    this.selectedRegion = 0
    this.selectedPriceDayOffset = 1
    this.selectedAvailabilityDayOffset = 0
    this.customSitePreferences = {
      ...(initialState.customSitePreferences || {}),
      // Remove those BT custom properties.
      BRAINTREE_PAYPAL_Billing_Agreement_Description:
        'Your money will only leave your account on the day of your delivery.',
    }
    this.customSitePreferencesLoading = new Set<string>()
    this.showCheckoutSummary = false
    this.customObjectDateOffsetAvailability = 0
    this.customObjectDateOffsetPricing = 0
    this.dataLoadedForAccuratePricingAndAvailabilty = false
    this.gtmId = initialState.gtmId
    this.gtmServer = initialState.gtmServer
    this.googlePayMerchantId = initialState.googlePayMerchantId
    this.googlePayMerchantName = initialState.googlePayMerchantName
    this.klarnaClientId = initialState.klarnaClientId
    this.klarnaCollectShipping = initialState.klarnaCollectShipping === 'true'
    this.sfccEnv = initialState.sfccEnv
    this.sentryInitialized = initialState.sentryInitialized
    this.sentryEnabled = initialState.sentryEnabled
    this.sentryEnv = initialState.sentryEnv
    this.sentryDSN = initialState.sentryDSN
    this.algoliaRefinements = []
    this.paypalClient = initialState.paypalClient
    this.appVersion = initialState.appVersion
    this.alertsUrl = initialState.alertsUrl
    this.affiliateTrackingCampaign = initialState.affiliateTrackingCampaign
    this.affiliateTrackingSource = initialState.affiliateTrackingSource
    this.affiliateTrackingMedium = initialState.affiliateTrackingMedium
    this.availablePaymentMethods = initialState.availablePaymentMethods || []
    this.hasApplePayButtonRendered = false
    this.hasKlarnaButtonRendered = false
    this.isRegionalIndexABTestEnabled = false
    this.regionalIndexesConfig = initialState.regionalIndexesConfig
    this.isShopTab = null

    makeObservable(this, {
      locale: observable,
      currency: observable,
      appHostname: observable,
      appOrigin: observable,
      deployTarget: observable,
      customerCacheClearInterval: observable,
      organizationId: observable,
      clientId: observable,
      siteId: observable,
      selectedStoreId: observable,
      selectedPriceDayOffset: observable,
      selectedAvailabilityDayOffset: observable,
      algoliaIndexBase: observable,
      algoliaIndexBaseDefault: observable,
      algoliaAnalyticsAPIKey: observable,
      algoliaAppId: observable,
      loqateAPIKey: observable,
      recaptchaSiteKey: observable,
      googleMapsAPIkey: observable,
      einsteinConfiguration: observable,
      customSitePreferences: observable,
      affiliateTrackingCampaign: observable,
      affiliateTrackingMedium: observable,
      affiliateTrackingSource: observable,
      showCheckoutSummary: observable,
      gtmId: observable,
      gtmServer: observable,
      sentryInitialized: observable,
      sentryEnabled: observable,
      sentryEnv: observable,
      sentryDSN: observable,
      paypalClient: observable,
      appVersion: observable,
      algoliaRefinements: observable,
      alertsUrl: observable,
      googlePayMerchantId: observable,
      googlePayMerchantName: observable,
      availablePaymentMethods: observable,
      hasApplePayButtonRendered: observable,
      isRegionalIndexABTestEnabled: observable,
      regionalIndexesConfig: observable,
      isShopTab: observable,
      storeAndDaySpecificNavigation: computed,
      setSelectedRegion: flow.bound,
      getAlgoliaRefinements: flow.bound,
      getCustomSitePreferencesByIds: flow.bound,
      getCustomSitePreferenceById: flow.bound,
      getCustomObjectDateOffsets: flow.bound,
      getAvailablePaymentMethods: flow.bound,
      setLocale: action,
      setCurrency: action,
      setDataLoadedForAccuratePricingAndAvailabilty: action,
      setSelectedStoreId: action.bound,
      setShowCheckoutSummary: action.bound,
      setAffiliateTrackingData: action.bound,
      setAffiliateTrackingDataForSession: action.bound,
      updateMenuItemCategory: action.bound,
      setSelectedPriceDay: action.bound,
      setSelectedAvailabilityDay: action.bound,
      isCustomSitePreferenceSet: action.bound,
      klarnaClientId: observable,
      sfccEnv: observable,
      klarnaCollectShipping: observable,
      setCustomSitePreferences: action.bound,
      setSentryInitialized: action.bound,
      setAlgoliaRefinements: action.bound,
      isPromoModalOpen: observable,
      setIsPromoModalOpen: action.bound,
      PromoModalOpen: computed,
      PromoModalPromo: computed,
      isAvailablePaymentMethodsSet: computed,
      setAvailablePaymentMethods: action.bound,
      filterAvailablePaymentMethods: action.bound,
      filterOrderEditAvailablePaymentMethods: action.bound,
      filterTopupAvailablePaymentMethods: action.bound,
      filterPreBookAvailablePaymentMethods: action.bound,
      hasKlarnaButtonRendered: observable,
      setHasApplePayButtonRendered: action.bound,
      setHasKlarnaButtonRendered: action.bound,
      setCustomObjectDateOffsetAvailability: action.bound,
      setCustomObjectDateOffsetPricing: action.bound,
      isKlarnaAvailable: action.bound,
      isClearpayAvailable: action.bound,
      initializeAlgoliaIndex: action.bound,
      setIsShopTab: action.bound
    })

    this.api = rootStore.api
    this.rootStore = rootStore

    this.searchClient = algoliasearch(this.algoliaAppId, this.algoliaApiKey)

    reaction(
      () => this.selectedStoreId,
      () => {
        this.dataLoadedForAccuratePricingAndAvailabilty = true;
      }
    )

    reaction(
      () => ({
        length: this.availablePaymentMethods.length === 0
      }),
      () => {
        // Trigger fetching available payment methods when there isn't any set
        if (typeof window !== undefined && !this.isAvailablePaymentMethodsSet) {
          this.getAvailablePaymentMethods()
        }
      }
    )
  }

  get asJson() {
    return {
      locale: this.locale,
      currency: this.currency,
      appHostname: this.appHostname,
      appOrigin: this.appOrigin,
      deployTarget: this.deployTarget,
      customerCacheClearInterval: this.customerCacheClearInterval,
      clientId: this.clientId,
      organizationId: this.organizationId,
      siteId: this.siteId,
      algoliaIndexBase: this.algoliaIndexBase,
      algoliaIndexBaseDefault: this.algoliaIndexBaseDefault,
      einsteinConfiguration: this.einsteinConfiguration,
      loqateAPIKey: this.loqateAPIKey,
      algoliaAppId: this.algoliaAppId,
      algoliaApiKey: this.algoliaApiKey,
      algoliaAnalyticsAPIKey: this.algoliaAnalyticsAPIKey,
      retailMediaCatalogID: this.retailMediaCatalogID,
      retailMediaContentStandardID: this.retailMediaContentStandardID,
      retailMediaUrl: this.retailMediaUrl,
      recaptchaSiteKey: this.recaptchaSiteKey,
      googleMapsAPIkey: this.googleMapsAPIkey,
      selectedPriceDayOffset: this.selectedPriceDayOffset,
      selectedAvailabilityDayOffset: this.selectedAvailabilityDayOffset,
      dataLoadedForAccuratePricingAndAvailabilty: this.dataLoadedForAccuratePricingAndAvailabilty,
      gtmId: this.gtmId,
      gtmServer: this.gtmServer,
      googlePayMerchantId: this.googlePayMerchantId,
      googlePayMerchantName: this.googlePayMerchantName,
      klarnaClientId: this.klarnaClientId,
      klarnaCollectShipping: this.klarnaCollectShipping,
      sentryInitialized: this.sentryInitialized,
      sentryEnabled: this.sentryEnabled,
      sentryEnv: this.sentryEnv,
      sentryDSN: this.sentryDSN,
      customSitePreferences: this.customSitePreferences,
      paypalClient: this.paypalClient,
      appVersion: this.appVersion,
      alertsUrl: this.alertsUrl,
      affiliateTrackingCampaign: this.affiliateTrackingCampaign,
      affiliateTrackingMedium: this.affiliateTrackingMedium,
      affiliateTrackingSource: this.affiliateTrackingSource,
      availablePaymentMethods: this.availablePaymentMethods,
      sfccEnv: this.sfccEnv
    }
  }

  get PromoModalOpen() {
    return this.isPromoModalOpen?.isOpen ?? false
  }

  get PromoModalPromo() {
    return this.isPromoModalOpen?.promotion
  }

  get isAvailablePaymentMethodsSet() {
    return this.availablePaymentMethods.length > 0
  }

  setIsPromoModalOpen(isOpen: boolean, promotion?: Promotion) {
    this.isPromoModalOpen = {isOpen, promotion}
  }

  setSelectedStoreId() {
    const storeId = this.rootStore.basketStore.basket?.c_deliveryStoreId
    const appliedPostCodeDeliverable = this.rootStore.basketStore.isAppliedPostCodeDeliverable
    this.setSelectedRegion()
    this.selectedStoreId = appliedPostCodeDeliverable ? storeId ?? '0' : '0'
  }

  *setSelectedRegion(isPostcodeAvailable?: boolean) {
    if (
      (typeof isPostcodeAvailable !== 'undefined' && !isPostcodeAvailable) ||
      this.rootStore.basketStore.isAppliedPostCodeDeliverable == null
    ) {
      this.selectedRegion = 0
      // Revert regionalIndexesConfig to empty if selectedRegion is 0
      this.regionalIndexesConfig = {} as RegionalIndexConfig
      return
    }

    let store: Nullable<StoreDetailResponseItem> = null

    if (this.rootStore.basketStore.basket?.c_deliveryStoreId == null) {
      return
    }

    if (this.rootStore.basketStore.basket?.c_deliveryStoreId !== this.selectedStoreId) {
      if (this.rootStore.basketStore.currentDeliveryStoreId) {
        store = yield flowResult(
          this.rootStore.storeDetailsStore.findStore(
            this.rootStore.basketStore.basket?.c_deliveryStoreId,
          ),
        )
      }

      this.selectedRegion =
        store?.c_priceBookRegion != null ? parseInt(store?.c_priceBookRegion, 10) : 0
    }
    // Check if the algolia index needs adjusting for regional merchandising
    this.setAlgoliaIndex()
  }

  // Set the Algolia Index to the regional one, if applicable
  setAlgoliaIndex() {
    const storedRegionalIndexesConfig = LocalStorage.available && LocalStorage.get(LOCALSTORAGE_KEYS.REGIONAL_INDEXES_CONFIG)
    const regionIndexInfo = this.regionalIndexesConfig || (storedRegionalIndexesConfig ? JSON.parse(storedRegionalIndexesConfig) as RegionalIndexConfig : null) || {}
    // Check the custom object and the custom preference, and make sure the customer is ranged
    const useRegionalIndex = this.selectedRegion !== 0 || regionIndexInfo?.isIndexEnabled || this.isRegionalIndexesEnabled
    // If we are not in region 0, and the selected region is enabled and we have a region name, set the index
    if (useRegionalIndex && regionIndexInfo?.isIndexEnabled && regionIndexInfo?.regionName) {
      this.algoliaIndexBase = `${this.algoliaIndexBaseDefault}__${regionIndexInfo.regionName}`
      return
    }

    // Put the index back to the default if required
    if (this.algoliaIndexBase !== this.algoliaIndexBaseDefault) {
      this.algoliaIndexBase = this.algoliaIndexBaseDefault
    }
}

setRegionalIndexConfig(config: RegionalIndexConfig) {
    if (config && config?.regionID) {
      // Check if the region is ranged and set the regional index config
      const region = config?.regionID
      if (region) {
        this.regionalIndexesConfig = {
          isRegionalIndexActive: config?.isRegionalIndexActive,
          regionID: config?.regionID,
          regionName: config?.regionName || '',
          isIndexEnabled: !!config?.isIndexEnabled,
          isABTestEnabled: !!config?.isABTestEnabled,
        }
        // Set the Algolia index after regional config is populated
        this.setAlgoliaIndex()
        this.setAlgoliaEnableABTest()
        // Set the config in local storage for persistence
        LocalStorage.available && LocalStorage.set(LOCALSTORAGE_KEYS.REGIONAL_INDEXES_CONFIG, JSON.stringify(this.regionalIndexesConfig))
      }
    } else {
      this.regionalIndexesConfig = {} as RegionalIndexConfig
      // Still need to set the Algolia index to default when no regional config is available
      this.setAlgoliaIndex()
      this.setAlgoliaEnableABTest()
      LocalStorage.available && LocalStorage.remove(LOCALSTORAGE_KEYS.REGIONAL_INDEXES_CONFIG)
    }
  }

  // Set the AB test flag for Algolia
  setAlgoliaEnableABTest() {
    this.isRegionalIndexABTestEnabled = !!this.regionalIndexesConfig?.isABTestEnabled
  }

  get isRegionalIndexesEnabled() {
    return this.customSitePreferences?.Algolia_UseRegionalIndexes as boolean
  }

  // Initialize Algolia index on load using stored config if available, if not, fetches config using stored postcode, if available
  initializeAlgoliaIndex() {
    const storedRegionalIndexesConfig = LocalStorage.available && LocalStorage.get(LOCALSTORAGE_KEYS.REGIONAL_INDEXES_CONFIG)
    if (storedRegionalIndexesConfig) {
      this.setRegionalIndexConfig(JSON.parse(storedRegionalIndexesConfig) as RegionalIndexConfig)
      return
    }
  }

  get badgesCustomPreferences() {
    return {
      topRightBadgeCustomPreference: this.customSitePreferences.isPdpTopRightTileBadgeEnabled,
      middleRightBadgeCustomPreference: this.customSitePreferences.isPdpMiddleRightTileBadgeEnabled,
      bottomRightBadgeCustomPreference: this.customSitePreferences.isPdpBottomRightTileBadgeEnabled,
    }
  }

  get storeAndDaySpecificNavigation() {
    const baseNavMenu = this.rootStore.contentStore.navMenu
    const routeMap = this.rootStore.contentStore.routeMap

    const currentCustomerGroups = this.rootStore.customerStore.currentCustomerGroups

    const filterMenu = (menuItems: NavigationMenu) => {
      const filteredMenu = <NavigationMenu>[]

      for (const menuItem of menuItems) {
        let shouldPush = true

        if(menuItem.customerGroups?.length) {
          shouldPush = menuItem.customerGroups.some(itemCustomerGroup =>
            currentCustomerGroups.some(userCustomerGroup => itemCustomerGroup === userCustomerGroup)
          );
        }

        if (shouldPush && menuItem.path) {
          const routeData = routeMap[menuItem.path]
          const category = routeData?.split('|')[2]
          this.updateMenuItemCategory(menuItem, category)
        } else if (!Array.isArray(menuItem.children) || !menuItem.children?.length) {
          shouldPush = false
        }

        let filteredChildren: NavigationMenu = []
        if (menuItem.children && Array.isArray(menuItem.children) && menuItem.children.length > 0) {
          filteredChildren = filterMenu(menuItem.children)
        }

        if (shouldPush || filteredChildren.length > 0) {
          const clonedMenuItem = {...menuItem}
          if (filteredChildren.length > 0) {
            clonedMenuItem.children = filteredChildren
          }
          filteredMenu.push(clonedMenuItem)
        }
      }

      return filteredMenu
    }

    const filteredMenu = filterMenu(baseNavMenu)
    return filteredMenu
  }

  updateMenuItemCategory = (menuItem: NavItem, categoryId: string) => {
    menuItem.categoryId = categoryId
  }

  getDiffInDays(offsetDays: number) {
    // When algolia is syncing everyday we need to use the customObjectDateOffsetAvailability and customObjectDateOffsetPricing
    const today = new Date()
    const tomorrow = new Date()
    tomorrow.setDate(today.getDate() + 1)
    const slotDate = this.rootStore.basketStore.basket?.c_windowStartTime

    // Set the date as tomorrow if there is no slot booked or an old slot is booked
    const deliveryDate = slotDate && new Date(slotDate) > today ? new Date(slotDate) : tomorrow
    const oneDay = 86400000 // 24 * 60 * 60 * 1000 // hours * minutes * seconds * milliseconds
    const diffInDays = offsetDays + Math.round(Math.abs((Number(deliveryDate) - Number(today)) / oneDay))

    return diffInDays
  }

  setSelectedPriceDay() {
    const newPriceDayOffset = this.getDiffInDays(this.customObjectDateOffsetPricing)
    // Only update if it has changed
    if (newPriceDayOffset != this.selectedPriceDayOffset) {
      this.selectedPriceDayOffset = newPriceDayOffset
    }
  }

  setSelectedAvailabilityDay() {
    const newAvailableDayOffset = this.getDiffInDays(this.customObjectDateOffsetAvailability)
    // Only update if it has changed
    if (newAvailableDayOffset != this.selectedAvailabilityDayOffset) {
      this.selectedAvailabilityDayOffset = newAvailableDayOffset
    }
  }

  setSentryInitialized(value: boolean) {
    this.sentryInitialized = value
  }

  setAlgoliaRefinements(value: string[]) {
    this.algoliaRefinements = value
  }

  setAffiliateTrackingData(headers: any) {
    try {
      if (headers['utm-campaign']) {
        this.affiliateTrackingCampaign = headers['utm-campaign']
      }
      if (headers['utm-medium']) {
        this.affiliateTrackingMedium = headers['utm-medium']
      }
      if (headers['utm-source']) {
        this.affiliateTrackingSource = headers['utm-source']
      }
    } catch (error) {
      console.error(error)
    }
  }

  setAffiliateTrackingDataForSession() {
    try {
      if (this.affiliateTrackingCampaign) {
        sessionStorage.setItem('affiliateTrackingCampaign', this.affiliateTrackingCampaign)
        this.affiliateTrackingCampaign = ''
      }
      if (this.affiliateTrackingMedium) {
        sessionStorage.setItem('affiliateTrackingMedium', this.affiliateTrackingMedium)
        this.affiliateTrackingMedium = ''
      }
      if (this.affiliateTrackingSource) {
        sessionStorage.setItem('affiliateTrackingSource', this.affiliateTrackingSource)
        this.affiliateTrackingSource = ''
      }
    } catch (error) {
      console.error(error)
    }
  }

  setDataLoadedForAccuratePricingAndAvailabilty() {
    this.dataLoadedForAccuratePricingAndAvailabilty = true
    this.getAlgoliaRefinements()
  }

  setLocale(locale: string) {
    if (this.locale != locale) {
      this.locale = locale
    }
  }

  setCurrency(currency: string) {
    if (this.currency != currency) {
      this.currency = currency
    }
  }

  setShowCheckoutSummary(show: boolean) {
    this.showCheckoutSummary = show
  }


  setCustomSitePreferences(value: CustomSitePreferences) {
    this.customSitePreferences = value
  }

  setIsShopTab(value: string | null) {
    if (value && this.isShopTab === null) {
      this.isShopTab = value === 'shoppingtab'
    }
  }

  isCustomSitePreferenceSet(preferenceId: string) {
    return preferenceId in this.customSitePreferences
  }

  *getCustomSitePreferenceById(preferenceId: string) {
    try {
      if (!preferenceId) {
        return
      }

      if (this.customSitePreferencesLoading.has(preferenceId)) {
        yield when(() => !this.customSitePreferencesLoading.has(preferenceId))
      }

      if(!this.isCustomSitePreferenceSet(preferenceId)){
        this.customSitePreferencesLoading.add(preferenceId)
        const response: Response = yield fetch(
          `${getAppOrigin()}/api/getCustomSitePreferenceById?preferenceId=${preferenceId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )

        if (!response.ok) {
          const {errMessage}: {errMessage: string} = yield response.json()

          throw new Error(
            'Error during fetching custom site preference: ' + preferenceId + ` ${errMessage}`,
          )
        }

        const result: Record<string, unknown> = yield response.json()

        this.setCustomSitePreferences({...this.customSitePreferences, ...(result || {})})
        this.customSitePreferencesLoading.delete(preferenceId)
      }

      return this.customSitePreferences[preferenceId]
    } catch (error) {
      console.error(error)
      return
    }
  }

  *getCustomSitePreferencesByIds(preferenceIds: string[]) {
    try {
      if (!preferenceIds.length) {
        return
      }
      const response: Response = yield fetch(`${getAppOrigin()}/api/getSitePreferences`, {
        method: 'POST',
        body: JSON.stringify({
          preferenceIds: preferenceIds,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const {errMessage}: {errMessage: string} = yield response.json()
        throw new Error(
          'Error during fetching custom site preferences: ' +
            preferenceIds.toString() +
            ' ' +
            errMessage,
        )
      }

      const result: Record<string, unknown> = yield response.json()
      this.setCustomSitePreferences({...this.customSitePreferences, ...(result || {})})
    } catch (error) {
      console.error(error)
    }
  }

  *getCustomObjectDateOffsets() {
    try {
      const response: {c_priceOffsetDays: number, c_availabilityOffsetDays: number} = yield this.api.shopperCustomObjects.getOffsetDatePricing({})
      // Handle Price offset
      if (Object.prototype.hasOwnProperty.call(response,'c_priceOffsetDays')) {
        this.setCustomObjectDateOffsetPricing(response.c_priceOffsetDays)
      }
      this.setSelectedPriceDay()

      // Handle Availability offset
      if (Object.prototype.hasOwnProperty.call(response,'c_availabilityOffsetDays')) {
        this.setCustomObjectDateOffsetAvailability(response.c_availabilityOffsetDays)
      }
      this.setSelectedAvailabilityDay()

      // Trigger Flag that we're ready for Search calls requireing availability and pricing data
      this.rootStore.globalStore.setDataLoadedForAccuratePricingAndAvailabilty()
    } catch (error) {
      console.error(error)
    }
  }

  *getAlgoliaRefinements() {
    const reqOptions = {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({day: this.selectedAvailabilityDayOffset, store: this.selectedStoreId}),
    }

    try {
      const response: Response = yield fetch(`${getAppOrigin()}/algolia/refinements`, reqOptions)

      if (response.ok) {
        const responseJson: Array<string> = yield response.json()

        this.setAlgoliaRefinements(responseJson)
      } else {
        console.error('Error response from API:', response.statusText)
      }
    } catch (error) {
      console.error(error)
    }
  }

  *getAvailablePaymentMethods() {
    try {
      const app = new BonusCardApp()
      const response: Response = yield fetch(
        `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Site-getAvailablePaymentMethods`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Send the app version as some payment methods are dependant on app version
          'x-transit-v': app.platform?.platformVersion || '',
          ...appHeaders(),
        },
      })

      if (!response.ok) {
        const { errMessage }: { errMessage: string } = yield response.json()
        throw new Error(`Error during fetching available payment methods: ${errMessage}`)
      }

      const {paymentMethods}: {paymentMethods:AvailablePaymentMethod[]} = yield response.json()

      this.setAvailablePaymentMethods(yield this.filterAvailablePaymentMethods(paymentMethods))

      return this.availablePaymentMethods

    } catch (error) {
      console.error('Error fetching payment methods:', error)
      // Reset to default payment methods if error to avoid loop of trying to set payment methods
      this.setAvailablePaymentMethods(yield this.filterAvailablePaymentMethods([]))
    }
  }

  setAvailablePaymentMethods(value: AvailablePaymentMethod[]) {
    this.availablePaymentMethods = value
  }

  setHasApplePayButtonRendered = (value:boolean) =>{
    this.hasApplePayButtonRendered = value;
  }

  setCustomObjectDateOffsetAvailability = (value:number) => {
    this.customObjectDateOffsetAvailability = value
  }

  setCustomObjectDateOffsetPricing = (value:number) => {
    this.customObjectDateOffsetPricing = value
  }


async isApplePayAvailable(): Promise<boolean> {
  let isAvailable = false
  try {
    await asyncScriptLoader(applePayScript)
  } catch (error) {
    console.error('Apple Pay script failed to load', error)
    return false
  }

  try{
    // @ts-ignore
    isAvailable = window.ApplePaySession && window.ApplePaySession.supportsVersion(SupportedApplePayVersion) && window.ApplePaySession.canMakePayments()
  }
  catch(err){
    isAvailable = false
    console.error("Error checking apple pay compatibility " + err)
  }

  return isAvailable
}

setHasKlarnaButtonRendered = (value:boolean) =>{
  this.hasKlarnaButtonRendered = value
}

async isKlarnaAvailable() {
  let isAvailable = false
  try {
    await asyncScriptLoader(klarnaScript)
    await asyncScriptLoader(klarnaOSMScript, {
      'data-environment': this.sfccEnv === 'production' ? 'production' : 'playground',
      'data-client-id': this.klarnaClientId
    })
    if (window.Klarna) {
      isAvailable = true
    }
  } catch (error) {
    console.error('Klarna script failed to load', error)
    return false
  }
  return isAvailable
}

async isClearpayAvailable() {
    let isAvailable = false
    const script = this.sfccEnv === 'production' ? clearpayScript : clearpaySandboxScript
  try {
    await asyncScriptLoader(script)
    if (window.AfterPay) {
      isAvailable = true
    }
  } catch (error) {
    console.error('clearpay script failed to load', error)
    return false
  }
  return isAvailable
}

filterTopupAvailablePaymentMethods() {
  let paymentMethods = this.availablePaymentMethods

  if (paymentMethods.length > 0) {
    paymentMethods = paymentMethods.filter((method) => !method.isTopupDisabled)
  }

  return paymentMethods
}

filterOrderEditAvailablePaymentMethods() {
  let paymentMethods = this.availablePaymentMethods

  if (paymentMethods.length > 0) {
    paymentMethods = paymentMethods.filter((method) => !method.isOrderEditDisabled)
  }

  return paymentMethods
}

filterPreBookAvailablePaymentMethods() {
  let paymentMethods = this.availablePaymentMethods

  if (paymentMethods.length > 0) {
    paymentMethods = paymentMethods.filter((method) => method.allowPrebook)
  }

  return paymentMethods
}


async filterAvailablePaymentMethods(paymentMethods:AvailablePaymentMethod[]) {

  const defaultPaymentMethods: AvailablePaymentMethod[] = [
    {
      id: PAYMENT_METHOD_IDS.BT_CREDIT_CARD,
      name: CARD_TYPES.CREDIT_CARD,
      isOrderEditDisabled: false,
      isTopupDisabled: false,
      allowPrebook: false
    },
    {
      id: PAYMENT_METHOD_IDS.PAYPAL,
      name: CARD_TYPES.PAYPAL,
      isOrderEditDisabled: false,
      isTopupDisabled: false,
      allowPrebook: false
    }
  ]

  const paymentMethodsToDisplay = paymentMethods.length > 0 ? paymentMethods : defaultPaymentMethods
  const klarnaAvailable = await this.isKlarnaAvailable()
  const applePayAvailable = await this.isApplePayAvailable();
  const clearpayAvailable = await this.isClearpayAvailable();

  const filteredPaymentMethods = paymentMethodsToDisplay
    .filter((method: AvailablePaymentMethod) =>
      !(method.id === PAYMENT_METHOD_IDS.APPLE_PAY && !applePayAvailable) &&
      !(method.id === PAYMENT_METHOD_IDS.KLARNA && !klarnaAvailable) &&
      !(method.id === PAYMENT_METHOD_IDS.CLEARPAY && !clearpayAvailable)
    )
    return filteredPaymentMethods
  }
}